import React from 'react';
import RelativeTime from '@/components/shared/RelativeTime';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faPen,
  faTrash,
  faThumbsUp,
  faComment
} from '@fortawesome/free-solid-svg-icons';
import {
  faThumbsUp as faThumbsUpRegular
} from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import IconButton from '@/components/ui/IconButton';
import placeholder_portrait_48x48 from "@/assets/imgs/placeholders/portrait_48x48.png";
import placeholder_portrait_40x40 from "@/assets/imgs/placeholders/portrait_40x40.png";
import ClampText from '@/components/shared/ClampText';
import SafeImg from '@/components/shared/SafeImg';
import IdolName from '@/components/ui/IdolName';
import { isImeComposingKey } from '@/shared/utils/imeKeyboard';

const SUB_COMMENTS_PREVIEW = 5;

const PostComments = ({
  processedCommentsData,
  commentState,
  commentHandlers,
  commentInputRefs,
  userInfo
}) => {
  const { t } = useTranslation();

  // 評論輸入框 refs（由 PostCard 傳入，送出後用來復原各自的高度）
  const { mainCommentTextareaRef, childCommentTextareaRefs } = commentInputRefs;

  // 解構組合對象
  const {
    commentText,
    childCommentTexts,
    childCommentVisibility,
    expandedSubComments,
    editingComments,
    editingSubComments
  } = commentState;

  const {
    setCommentText,
    setChildCommentTexts,
    onCommentSubmit,
    onCommentIconClick,
    onChildComment,
    onKeyDown,
    onEditCommentClick,
    onCancelEditComment,
    onCommentContentChange,
    onRequestSaveEdit,
    onDeleteComment,
    onLike,
    onLoadMoreComments,
    onToggleSubComments,
    onAutoResizeTextarea
  } = commentHandlers;

  const { avatarUrl, idolId, isOwner } = userInfo;

  // 編輯留言送出前跳「確定要儲存嗎？」確認（紙飛機按鈕與 Enter 共用）；帶上這個編輯框的留言 id，
  // 同時開著多個編輯框時才會存到使用者按送出的那一則
  const requestSaveEdit = (commentId, content) => {
    if (content.trim()) {
      onRequestSaveEdit(commentId);
    }
  };

  // 編輯框 Enter 等同按紙飛機，與新增留言一致；Shift+Enter 換行，輸入法選字的 Enter 不觸發
  const handleEditKeyDown = (event, commentId, content) => {
    if (event.key === 'Enter' && !event.shiftKey && !isImeComposingKey(event)) {
      event.preventDefault();
      requestSaveEdit(commentId, content);
    }
  };

  // 取得可見子評論
  const getVisibleSubComments = (comment) => {
    const subComments = comment.comment_sub_comments || [];
    const isExpanded = expandedSubComments[comment.comment_id];

    return {
      visible: isExpanded ? subComments : subComments.slice(0, SUB_COMMENTS_PREVIEW),
      hasMore: subComments.length > SUB_COMMENTS_PREVIEW,
      remaining: Math.max(0, subComments.length - SUB_COMMENTS_PREVIEW),
      isExpanded
    };
  };

  // 渲染子評論
  const renderSubComments = (comment) => {
    const subCommentData = getVisibleSubComments(comment);

    if (!comment.comment_sub_comments || comment.comment_sub_comments.length === 0) {
      return null;
    }

    return (
      <>
        {subCommentData.visible.map((item) => (
          <div key={item.comment_id} className="message-item child-merge-l d-flex gap-3 py-2 px-3">
            <div className="img-sm flex-shrink-0">
              <SafeImg
                className="rounded-pill img-fluid"
                src={item.idol_avatar_url || placeholder_portrait_40x40}
                alt={item.idol_nick_name}
              />
            </div>
            <div className="user-message w-100 min-w-0 position-relative">
              <div className="bg-light-gray rounded p-2">
                <div className="name mb-1 position-relative">
                  <a href={`/idol/${item.idol_uuid}`} className="link-none hover-p d-inline-flex align-items-center mw-100">
                    <IdolName name={item.idol_nick_name} isOfficial={item.idol_is_official} />
                  </a>
                </div>
                {editingSubComments[item.comment_id]?.editing ? (
                  <div className="user-message w-100 min-w-0 position-relative">
                    <span className="user-submit position-absolute">
                      <IconButton
                        tooltip={t('send')}
                        size="sm"
                        disabled={!(editingSubComments[item.comment_id]?.content || '').trim()}
                        onClick={() => requestSaveEdit(item.comment_id, editingSubComments[item.comment_id]?.content || '')}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} />
                      </IconButton>
                    </span>
                    <Form.Control
                      className="user-textarea"
                      as="textarea"
                      rows={1}
                      value={editingSubComments[item.comment_id]?.content || ''}
                      ref={(el) => {
                        if (el) {
                          el.style.height = 'auto';
                          el.style.height = el.scrollHeight + 'px';
                        }
                      }}
                      onChange={(e) => {
                        onCommentContentChange(item.comment_id, e.target.value, true);
                        onAutoResizeTextarea(e);
                      }}
                      onKeyDown={(e) => handleEditKeyDown(e, item.comment_id, editingSubComments[item.comment_id]?.content || '')}
                    />
                  </div>
                ) : (
                  <div className="message-text">
                    <ClampText
                      text={item.comment_content}
                      className="post-text comment-content"
                      clampClassName="comment-container"
                    />
                  </div>
                )}
              </div>
              <div className="d-flex align-items-center">
                <small className="text-gray me-2"><RelativeTime value={item.created_at} /></small>
                {editingSubComments[item.comment_id]?.editing ? (
                  <button
                    className="btn-link link-primary p-0 border-0 bg-transparent small cursor-p"
                    onClick={() => onCancelEditComment(item.comment_id, true)}
                    style={{ textDecoration: 'none' }}
                  >
                    {t('cancel')}
                  </button>
                ) : (
                  <>
                    <IconButton tooltip={t('like')} size="sm" onClick={() => onLike('comment', item.comment_id)}>
                      <FontAwesomeIcon
                        icon={item.comment_is_like ? faThumbsUp : faThumbsUpRegular}
                        className={item.comment_is_like ? 'text-primary' : 'text-gray'}
                      />
                    </IconButton>
                    {idolId === item.idol_id && (
                      <IconButton tooltip={t('edit')} size="sm" onClick={() => onEditCommentClick(item, true)}>
                        <FontAwesomeIcon icon={faPen} className="link-gray" />
                      </IconButton>
                    )}
                    {(idolId === item.idol_id || isOwner) && (
                      <IconButton tooltip={t('delete')} size="sm" onClick={() => onDeleteComment(item.comment_id)}>
                        <FontAwesomeIcon icon={faTrash} className="link-gray" />
                      </IconButton>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

        {subCommentData.hasMore && !subCommentData.isExpanded && (
          <div className="text-center mt-2">
            <button
              className="btn-link text-primary p-0 border-0 bg-transparent small mt-1 see-more-btn"
              onClick={() => onToggleSubComments(comment.comment_id)}
              style={{ textDecoration: 'none' }}
            >
              {`${t('see_more')} (${subCommentData.remaining} ${t('Comment')})`}
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="message-section">
      {processedCommentsData.visible.length > 0 ? (
        <>
          {processedCommentsData.visible.map((comment) => {
            const isTipComment = comment.comment_is_tip;

            return (
              <React.Fragment key={comment.comment_id}>
                <div className="message-item d-flex gap-3 py-2 px-3">
                  <div className="img-sm2 flex-shrink-0">
                    <SafeImg
                      src={comment.idol_avatar_url || placeholder_portrait_48x48}
                      alt={comment.idol_nick_name}
                      className="img-fluid rounded-pill"
                    />
                  </div>
                  <div className="user-message w-100 min-w-0 position-relative">
                    <div className={`${isTipComment ? 'tip-gradient' : 'bg-light-gray'} rounded p-2`}>
                      <div className="name mb-1 position-relative">
                        <a href={`/idol/${comment.idol_uuid}`} className="link-none hover-p d-inline-flex align-items-center mw-100">
                          <IdolName name={comment.idol_nick_name} isOfficial={comment.idol_is_official} />
                        </a>
                      </div>
                      {!editingComments[comment.comment_id]?.editing && (
                        <div className="message-text">
                          <ClampText
                            text={comment.comment_content}
                            className="post-text comment-content"
                            clampClassName="comment-container"
                            prefix={comment.comment_is_tip && comment.comment_tip_amount ? (
                              <span className="badge rounded-pill text-white me-2 bg-primary fw-semibold">
                                <FontAwesomeIcon icon="fa-solid fa-donate" className="me-1" />
                                ₣{comment.comment_tip_amount}
                              </span>
                            ) : null}
                          />
                        </div>
                      )}
                      {editingComments[comment.comment_id]?.editing && (
                        <div className="user-message w-100 min-w-0 position-relative">
                          <span className="user-submit position-absolute">
                            <IconButton
                              tooltip={t('send')}
                              size="sm"
                              disabled={!(editingComments[comment.comment_id]?.content || '').trim()}
                              onClick={() => requestSaveEdit(comment.comment_id, editingComments[comment.comment_id]?.content || '')}
                            >
                              <FontAwesomeIcon icon={faPaperPlane} />
                            </IconButton>
                          </span>
                          <Form.Control
                            className="user-textarea"
                            as="textarea"
                            rows={1}
                            value={editingComments[comment.comment_id]?.content || ''}
                            ref={(el) => {
                              if (el) {
                                el.style.height = 'auto';
                                el.style.height = el.scrollHeight + 'px';
                              }
                            }}
                            onChange={(e) => {
                              onCommentContentChange(comment.comment_id, e.target.value);
                              onAutoResizeTextarea(e);
                            }}
                            onKeyDown={(e) => handleEditKeyDown(e, comment.comment_id, editingComments[comment.comment_id]?.content || '')}
                          />
                        </div>
                      )}
                    </div>
                    <div className="d-flex align-items-center">
                      <small className="text-gray me-2"><RelativeTime value={comment.created_at} /></small>
                      {editingComments[comment.comment_id]?.editing ? (
                        <button
                          className="btn-link link-primary p-0 border-0 bg-transparent small cursor-p"
                          onClick={() => onCancelEditComment(comment.comment_id)}
                          style={{ textDecoration: 'none' }}
                        >
                          {t('cancel')}
                        </button>
                      ) : (
                        <>
                          <IconButton tooltip={t('like')} size="sm" onClick={() => onLike('comment', comment.comment_id)}>
                            <FontAwesomeIcon
                              icon={comment.comment_is_like ? faThumbsUp : faThumbsUpRegular}
                              className={comment.comment_is_like ? 'text-primary' : 'text-gray'}
                            />
                          </IconButton>
                          <IconButton tooltip={t('Comment')} size="sm" onClick={() => onCommentIconClick(comment.comment_id)}>
                            <FontAwesomeIcon icon={faComment} className="link-gray" />
                          </IconButton>
                          {idolId === comment.idol_id && (
                            <IconButton tooltip={t('edit')} size="sm" onClick={() => onEditCommentClick(comment)}>
                              <FontAwesomeIcon icon={faPen} className="link-gray" />
                            </IconButton>
                          )}
                          {(idolId === comment.idol_id || isOwner) && (
                            <IconButton tooltip={t('delete')} size="sm" onClick={() => onDeleteComment(comment.comment_id)}>
                              <FontAwesomeIcon icon={faTrash} className="link-gray" />
                            </IconButton>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* 子評論 */}
                {renderSubComments(comment)}

                {/* 子評論輸入框 */}
                {childCommentVisibility[comment.comment_id] && (
                  <div className="message-item child-merge-l d-flex gap-3 py-2 px-3 mb-3">
                    <div className="img-sm flex-shrink-0">
                      <SafeImg
                        src={avatarUrl || placeholder_portrait_48x48}
                        alt="User avatar"
                        className="img-fluid rounded-pill"
                      />
                    </div>
                    <div className="user-message w-100 min-w-0 position-relative">
                      <span className="user-submit position-absolute">
                        <IconButton
                          tooltip={t('send')}
                          size="sm"
                          disabled={!(childCommentTexts[comment.comment_id] || '').trim()}
                          onClick={() => {
                            const content = childCommentTexts[comment.comment_id] || '';
                            if (content.trim()) {
                              onChildComment(comment.comment_id);
                            }
                          }}
                        >
                          <FontAwesomeIcon icon={faPaperPlane} />
                        </IconButton>
                      </span>
                      <Form.Control
                        className="user-textarea"
                        as="textarea"
                        rows={1}
                        ref={(el) => { childCommentTextareaRefs.current[comment.comment_id] = el; }}
                        value={childCommentTexts[comment.comment_id] || ''}
                        onChange={(e) => {
                          setChildCommentTexts(prev => ({ ...prev, [comment.comment_id]: e.target.value }));
                          onAutoResizeTextarea(e);
                        }}
                        onKeyDown={(e) => onKeyDown(e, comment.comment_id)}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* 載入更多評論按鈕 */}
          {processedCommentsData.hasMore && (
            <div className="text-center mt-3">
              <button
                className="btn-link text-primary p-0 border-0 bg-transparent small load-more-btn"
                onClick={onLoadMoreComments}
                style={{ textDecoration: 'none' }}
              >
                {`${t('load_more_comments')} (${processedCommentsData.remaining} ${t('Comment')})`}
              </button>
            </div>
          )}
        </>
      ) : null}

      {/* 一般評論輸入框 */}
      <div className="message-item myself d-flex gap-3 py-2 px-3 pb-3">
        <div className="img-sm2 flex-shrink-0">
          <SafeImg
            src={avatarUrl || placeholder_portrait_48x48}
            alt="User avatar"
            className="img-fluid rounded-pill"
          />
        </div>
        <div className="user-message w-100 min-w-0 position-relative p-0">
          <span className="user-submit position-absolute">
            <IconButton
              tooltip={t('send')}
              size="sm"
              disabled={!commentText.trim()}
              onClick={() => commentText.trim() && onCommentSubmit(null)}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </IconButton>
          </span>
          <Form.Control
            className="user-textarea"
            as="textarea"
            rows={1}
            ref={mainCommentTextareaRef}
            value={commentText}
            onChange={(e) => {
              setCommentText(e.target.value);
              onAutoResizeTextarea(e);
            }}
            onKeyDown={onKeyDown}
          />
        </div>
      </div>
    </div>
  );
};

export default PostComments;
